// src/utils/downloadUtils.ts
import dayjs from "dayjs";
import { downloadReportPdf, downloadOrderPdf, downloadReportDefaultPdf, downloadOrderDefaultPdf } from "./pdfDownload";
import { downloadReportExcel, downloadOrderExcel, downloadMultipleReportsImagesZip, downloadMultipleOrdersImagesZip } from "./fileDownload";

export interface DownloadConfig {
    id: number;
    destinationName?: string | null;
    workDate?: string | null | Date;
    setLoading?: (isLoading: boolean) => void;
    onSuccess?: () => void;
    onError?: (error: Error) => void;
}

export interface DownloadMultipleImagesConfig {
    ids: number[];
    reportType?: number; // 報告書の種類を追加
    setLoading?: (isLoading: boolean) => void;
    onSuccess?: () => void;
    onError?: (error: Error) => void;
}

/**
 * PDFファイルをダウンロードする共通関数
 * @param config ダウンロード設定
 * @param type ダウンロードタイプ（'report' または 'order'）
 */
export const downloadPdf = async (
    config: DownloadConfig,
    type: 'report' | 'order'
) => {
    const { id, destinationName, workDate, setLoading, onSuccess, onError } = config;

    // ローディング開始
    if (setLoading) setLoading(true);

    try {
        // 日付部分の生成
        const datePart = workDate ? dayjs(workDate).format('YYYYMMDD') : '日時未設定';

        // 店舗名に「様」を付ける
        const nameWithSuffix = destinationName ? `${destinationName}様` : '搬入先未設定';

        // ファイル名の生成 (日付_店舗名様)
        const fileName = `${datePart}_${nameWithSuffix}`;

        // タイプに応じて適切なダウンロード関数を呼び出す
        let response;
        if (type === 'report') {
            response = await downloadReportPdf({ id, fileName });
        } else {
            response = await downloadOrderPdf({ id, fileName });
        }

        if (!response) {
            throw new Error("ダウンロード処理に失敗しました");
        }

        // 成功時のコールバック
        if (onSuccess) onSuccess();

    } catch (error) {
        console.error("PDFダウンロード失敗", error);

        // エラー処理
        if (onError) {
            onError(error instanceof Error ? error : new Error("PDFファイルのダウンロードに失敗しました"));
        } else {
            alert("PDFファイルのダウンロードに失敗しました。再度お試しください。");
        }
    } finally {
        // ローディング終了
        if (setLoading) setLoading(false);
    }
};

/**
 * PDFファイルをダウンロードする共通関数
 * @param config ダウンロード設定
 * @param type ダウンロードタイプ（'report' または 'order'）
 */
export const downloadDefaultPdf = async (
    config: DownloadConfig,
    type: 'report' | 'order'
) => {
    const { id, destinationName, workDate, setLoading, onSuccess, onError } = config;

    // ローディング開始
    if (setLoading) setLoading(true);

    try {
        // 日付部分の生成
        const datePart = workDate ? dayjs(workDate).format('YYYYMMDD') : '日時未設定';

        // 店舗名に「様」を付ける
        const nameWithSuffix = destinationName ? `${destinationName}様(現場提出時)` : '搬入先未設定(現場提出時)';

        // ファイル名の生成 (日付_店舗名様)
        const fileName = `${datePart}_${nameWithSuffix}`;

        // タイプに応じて適切なダウンロード関数を呼び出す
        let response;
        if (type === 'report') {
            response = await downloadReportDefaultPdf({ id, fileName });
        } else {
            response = await downloadOrderDefaultPdf({ id, fileName });
        }

        if (!response) {
            throw new Error("ダウンロード処理に失敗しました");
        }

        // 成功時のコールバック
        if (onSuccess) onSuccess();

    } catch (error) {
        console.error("PDFダウンロード失敗", error);

        // エラー処理
        if (onError) {
            onError(error instanceof Error ? error : new Error("PDFファイルのダウンロードに失敗しました"));
        } else {
            alert("PDFファイルのダウンロードに失敗しました。再度お試しください。");
        }
    } finally {
        // ローディング終了
        if (setLoading) setLoading(false);
    }
};

/**
 * Excelファイルをダウンロードする共通関数
 * @param config ダウンロード設定
 * @param type ダウンロードタイプ（'report' または 'order'）
 */
export const downloadExcel = async (
    config: DownloadConfig,
    type: 'report' | 'order'
) => {
    const { id, destinationName, workDate, setLoading, onSuccess, onError } = config;

    // ローディング開始
    if (setLoading) setLoading(true);

    try {
        // 日付部分の生成
        const datePart = workDate ? dayjs(workDate).format('YYYYMMDD') : '日時未設定';

        // 店舗名に「様」を付ける
        const nameWithSuffix = destinationName ? `${destinationName}様` : '搬入先未設定';

        // ファイル名の生成 (日付_店舗名様)
        const fileName = `${datePart}_${nameWithSuffix}`;

        // タイプに応じて適切なダウンロード関数を呼び出す
        let response;
        if (type === 'report') {
            response = await downloadReportExcel({ id, fileName });
        } else {
            response = await downloadOrderExcel({ id, fileName });
        }

        if (!response) {
            throw new Error("ダウンロード処理に失敗しました");
        }

        // 成功時のコールバック
        if (onSuccess) onSuccess();

    } catch (error) {
        console.error("Excelダウンロード失敗", error);

        // エラー処理
        if (onError) {
            onError(error instanceof Error ? error : new Error("Excelファイルのダウンロードに失敗しました"));
        } else {
            alert("Excelファイルのダウンロードに失敗しました。再度お試しください。");
        }
    } finally {
        // ローディング終了
        if (setLoading) setLoading(false);
    }
};

/**
 * 複数の画像をZIPファイルでダウンロードする共通関数
 * @param config ダウンロード設定
 * @param type ダウンロードタイプ（'report' または 'order'）
 */
export const downloadMultipleImagesZip = async (
    config: DownloadMultipleImagesConfig,
    type: 'report' | 'order'
) => {
    const { ids, reportType, setLoading, onSuccess, onError } = config;

    if (!ids || ids.length === 0) {
        if (onError) {
            onError(new Error("ダウンロード対象が選択されていません"));
        }
        return;
    }

    // ローディング開始
    if (setLoading) setLoading(true);

    try {
        // ファイル名の生成
        const datePart = dayjs().format('YYYYMMDD');

        let typeLabel = '';
        if (type === 'report') {
            // 報告書の種類に応じたラベル
            switch (reportType) {
                case 1:
                    typeLabel = 'エアコン報告書';
                    break;
                case 2:
                    typeLabel = 'フード報告書';
                    break;
                case 3:
                    typeLabel = 'フリー報告書';
                    break;
                case 4:
                    typeLabel = '床報告書';
                    break;
                default:
                    typeLabel = '報告書';
                    break;
            }
        } else {
            typeLabel = '受注書';
        }

        const fileName = `${typeLabel}画像_${datePart}`;

        // タイプに応じて適切なダウンロード関数を呼び出す
        let response;
        if (type === 'report') {
            response = await downloadMultipleReportsImagesZip({ ids, fileName });
        } else {
            response = await downloadMultipleOrdersImagesZip({ ids, fileName });
        }

        if (!response) {
            throw new Error("ダウンロード処理に失敗しました");
        }

        // 成功時のコールバック
        if (onSuccess) onSuccess();

    } catch (error) {
        console.error("複数画像ZIPダウンロード失敗", error);

        // エラー処理
        if (onError) {
            onError(error instanceof Error ? error : new Error("画像ファイルのダウンロードに失敗しました"));
        } else {
            alert("画像ファイルのダウンロードに失敗しました。再度お試しください。");
        }
    } finally {
        // ローディング終了
        if (setLoading) setLoading(false);
    }
};